/**
 * BoothMatchIndicator - Badge showing booth match percentage
 * Issue #677 - Vendor Booth Preferences & Smart Matching
 * Issue #791 - Added tooltip explanations for match criteria
 */

import React from 'react';
import { Target } from 'lucide-react';
import Tooltip from './Tooltip';

interface BoothMatchIndicatorProps {
  score: number; // 0-100
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
}

export default function BoothMatchIndicator({
  score,
  size = 'medium',
  showLabel = true,
}: BoothMatchIndicatorProps) {
  // Clamp score to 0-100
  const clampedScore = Math.max(0, Math.min(100, score));

  // Determine color based on score
  const getColorClasses = () => {
    if (clampedScore >= 80) {
      return {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-300',
        ring: 'ring-green-500',
      };
    } else if (clampedScore >= 60) {
      return {
        bg: 'bg-moonbone',
        text: 'text-ink',
        border: 'border-teal-300',
        ring: 'ring-dusty-teal',
      };
    } else if (clampedScore >= 40) {
      return {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        border: 'border-yellow-300',
        ring: 'ring-yellow-500',
      };
    } else {
      return {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-300',
        ring: 'ring-gray-500',
      };
    }
  };

  const colors = getColorClasses();

  // Size variants
  const sizeClasses = {
    small: {
      container: 'px-2 py-1',
      icon: 'w-3 h-3',
      text: 'text-xs',
      score: 'text-sm',
    },
    medium: {
      container: 'px-3 py-1.5',
      icon: 'w-4 h-4',
      text: 'text-sm',
      score: 'text-base',
    },
    large: {
      container: 'px-4 py-2',
      icon: 'w-5 h-5',
      text: 'text-base',
      score: 'text-lg',
    },
  }[size];

  const getMatchLabel = () => {
    if (clampedScore >= 80) return 'Excellent Match';
    if (clampedScore >= 60) return 'Good Match';
    if (clampedScore >= 40) return 'Fair Match';
    return 'Weak Match';
  };

  const getTooltipContent = () => {
    if (clampedScore >= 80) {
      return 'This booth closely matches your preferences including budget, location, size, and amenities. We highly recommend considering this option.';
    } else if (clampedScore >= 60) {
      return 'This booth matches most of your key preferences. It may be a good fit depending on your priorities.';
    } else if (clampedScore >= 40) {
      return 'This booth matches some of your preferences. Consider reviewing the specific match details below.';
    } else {
      return 'This booth has limited alignment with your stated preferences. Review the details to see if it still meets your needs.';
    }
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <Tooltip content={getTooltipContent()} position="top" allowWrap maxWidth="250px">
        <div
          className={`flex items-center gap-1.5 ${sizeClasses.container} ${colors.bg} ${colors.border} border rounded-full font-semibold ${colors.text} cursor-help`}
        >
          <Target className={sizeClasses.icon} />
          <span className={sizeClasses.score}>{Math.round(clampedScore)}%</span>
        </div>
      </Tooltip>
      {showLabel && (
        <span className={`${sizeClasses.text} font-medium ${colors.text}`}>
          {getMatchLabel()}
        </span>
      )}
    </div>
  );
}
